import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Handshake, ShieldCheck, TrendingUp, MapPin } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const values = [
  {
    icon: ShieldCheck,
    title: "Quality Assured",
    desc: "Every product undergoes rigorous quality checks before export.",
  },
  {
    icon: Handshake,
    title: "Trusted Partnerships",
    desc: "Long-standing relationships with suppliers and buyers worldwide.",
  },
  {
    icon: TrendingUp,
    title: "Market Expertise",
    desc: "Deep knowledge of global leather and agro commodity markets.",
  },
  {
    icon: MapPin,
    title: "Strategic Location",
    desc: "Based in Kolkata — India's historic hub for leather and agro trade.",
  },
];

export default function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={ref} className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left: Image + accent */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-center"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/0za5pvs1_LOGO2.jpeg"
              alt="Raihan International Logo"
              data-testid="about-image"
              className="w-80 md:w-96 lg:w-[28rem] h-auto object-contain"
            />
          </motion.div>

          {/* Right: Text */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            <p
              className="font-accent text-[#4A6741] text-lg"
              data-testid="about-accent"
            >
              Who we are
            </p>
            <h2
              data-testid="about-heading"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1614] tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The Name Behind
              <br />
              Premium Indian Exports
            </h2>
            <Separator className="w-16 h-0.5 bg-[#D4AF37]" />
            <p
              data-testid="about-description"
              className="text-[#1A1614]/70 text-base leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Raihan International is a Kolkata-based export house dealing in premium leather and fresh agro produce.
              We started with a straightforward conviction — that international buyers deserve a partner who is consistent, reachable, and honest about what they can deliver.
            </p>
            <div className="space-y-4">
              <div>
                <h3
                  className="text-lg font-semibold text-[#8D5A34] mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Leather
                </h3>
                <p
                  className="text-[#1A1614]/70 text-base leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  We deal in wet-blue hides, finished leathers & leather goods, working to strict grading standards with full documentation from origin to delivery. Quality that holds up — not just on paper, but when the cargo arrives.
                </p>
              </div>
              <div>
                <h3
                  className="text-lg font-semibold text-[#4A6741] mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Agro
                </h3>
                <p
                  className="text-[#1A1614]/70 text-base leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  We export fresh seasonal fruits and vegetables, and import select commodities, including dragon fruit and apples into India. Every consignment is handled with the care that perishable goods demand — properly graded, properly documented, and on time.
                </p>
              </div>
            </div>
            <p
              className="text-[#1A1614] text-base leading-relaxed font-medium italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Consistent quality. Direct communication. No surprises. That's how we build partnerships that last.
            </p>
          </motion.div>
        </div>

        {/* Values grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-24"
          data-testid="about-values"
        >
          {values.map((v, i) => (
            <div
              key={i}
              data-testid={`about-value-${i}`}
              className="glass-card rounded-xl p-6 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-full bg-[#8D5A34]/10 flex items-center justify-center mb-4 group-hover:bg-[#8D5A34]/20 transition-colors">
                <v.icon size={22} strokeWidth={1.5} className="text-[#8D5A34]" />
              </div>
              <h3
                className="text-lg font-semibold text-[#1A1614] mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {v.title}
              </h3>
              <p
                className="text-sm text-[#1A1614]/60 leading-relaxed"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {v.desc}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
